import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { TicketFlowStepper } from '@/components/shared/TicketFlowStepper';
import IssueDescription from '@/components/shared/IssueDescription';
import { Checkbox } from '@/components/ui/checkbox';
import { useIssueCategories } from '@/hooks/useProducts';
import { toast } from 'sonner';
import { ticketsApi, usersApi, jobsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { 
  ArrowLeft, PencilSimple, ChatCircle, FloppyDisk, X,
  User, Phone, MapPin, Package, Clock, CalendarBlank,
  ShieldCheck, CheckSquare, XCircle, HandWaving, UserPlus, Star, ClipboardText, ArrowClockwise
} from '@phosphor-icons/react';

const REJECTION_REASONS = [
  'Invalid serial number',
  'Product not under warranty',
  'Duplicate ticket',
  'Insufficient information',
  'Service not covered',
  'Other',
];

const parseDescription = (desc) => {
  if (!desc) return { selected: [], raw: '' };
  
  const cleanDesc = desc.replace(/\s*\[WARRANTY STATUS: EXPIRED\]\s*$/, '').trim();
  
  const issuesMatch = cleanDesc.match(/Selected Issues:\s*\n?([\s\S]*?)(?:\n\n|$)/);
  const descMatch = cleanDesc.match(/(?:Customer|Caller) Description:\s*\n?([\s\S]*?)$/);
  
  let selected = [];
  let raw = cleanDesc;
  
  if (issuesMatch) {
    selected = issuesMatch[1]
      .split('\n')
      .map(s => s.replace(/^[•\-\*]\s*/, '').trim())
      .filter(Boolean);
    
    if (descMatch) {
      raw = descMatch[1].trim();
    } else {
      const parts = cleanDesc.split(/\n\n(?:Customer|Caller)?\s*Description:\s*/i);
      if (parts.length > 1) {
        raw = parts[1].trim();
      } else {
        const issuesBlockEnd = cleanDesc.indexOf(issuesMatch[0]) + issuesMatch[0].length;
        raw = cleanDesc.substring(issuesBlockEnd).trim();
      }
    }
  } else if (descMatch) {
    raw = descMatch[1].trim();
  }
  
  return { selected, raw };
};

const compileDescription = (selected, raw, createdByRole, isExpired) => {
  const issuesBlock = selected.length > 0 
    ? `Selected Issues:\n${selected.map(i => `• ${i}`).join('\n')}\n\n`
    : '';
  const descLabel = (createdByRole === 'agent') ? 'Caller Description' : 'Customer Description';
  let desc = `${issuesBlock}${descLabel}:\n${raw}`;
  if (isExpired) {
    desc = `${desc}\n\n[WARRANTY STATUS: EXPIRED]`;
  }
  return desc;
};

export const AdminTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const currentUser = useAppStore((state) => state.currentUser);
  const viewAsRole = useAppStore((state) => state.viewAsRole);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';

  // Role scopes
  const effectiveRole = (currentUser?.role === 'super_admin' && viewAsRole) ? viewAsRole : currentUser?.role;
  const effectiveUser = (currentUser?.role === 'super_admin' && viewAsUser) ? viewAsUser : currentUser;

  const canEdit = effectiveRole === 'super_admin' || effectiveRole === 'agent' || effectiveRole === 'technician';
  const isAgentOrAdmin = effectiveRole === 'agent' || effectiveRole === 'super_admin';
  const isFocOrAdmin = effectiveRole === 'foc' || effectiveRole === 'super_admin';

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Comment state
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Description structured edit state
  const [selectedIssueLabels, setSelectedIssueLabels] = useState([]);
  const [rawDescText, setRawDescText] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  // Reject state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNote, setRejectionNote] = useState('');
  const [paymentRotation, setPaymentRotation] = useState(0);
  const [lightboxPhotoUrl, setLightboxPhotoUrl] = useState(null);

  // Assign state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(false);
  const [selectedTechId, setSelectedTechId] = useState('');

  // Reassign state
  const [reassignRole, setReassignRole] = useState(null); // 'agent', 'foc', 'technician'
  const [reassignUsers, setReassignUsers] = useState([]);
  const [loadingReassignUsers, setLoadingReassignUsers] = useState(false);
  const [selectedReassignUserId, setSelectedReassignUserId] = useState('');
  const [submittingReassign, setSubmittingReassign] = useState(false);

  const { data: issueCategories } = useIssueCategories(ticket?.product_category_id);

  const fetchTicket = async () => {
    try {
      const { data } = await ticketsApi.get(id);
      setTicket(data);
      
      const parsed = parseDescription(data.description);
      setSelectedIssueLabels(parsed.selected);
      setRawDescText(parsed.raw);
      
      const hasExpiredMarker = data.description?.includes('[WARRANTY STATUS: EXPIRED]');
      setIsExpired(hasExpiredMarker);

      setEditForm({
        description: data.description,
        priority: data.priority,
        service_address: data.service_address || '',
      });
    } catch (err) {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);

  const handleIssueToggle = (label) => {
    let next;
    if (selectedIssueLabels.includes(label)) {
      next = selectedIssueLabels.filter(l => l !== label);
    } else {
      next = [...selectedIssueLabels, label];
    }
    setSelectedIssueLabels(next);
    const compiled = compileDescription(next, rawDescText, ticket?.created_by_role, isExpired);
    setEditForm(prev => ({ ...prev, description: compiled }));
  };

  const handleRawDescChange = (text) => {
    setRawDescText(text);
    const compiled = compileDescription(selectedIssueLabels, text, ticket?.created_by_role, isExpired);
    setEditForm(prev => ({ ...prev, description: compiled }));
  };

  const handleCancelEdit = () => {
    setEditing(false);
    if (ticket) {
      const parsed = parseDescription(ticket.description);
      setSelectedIssueLabels(parsed.selected);
      setRawDescText(parsed.raw);
      setEditForm(prev => ({ ...prev, description: ticket.description }));
    }
  };

  const getBackRoute = () => {
    switch (effectiveRole) {
      case 'customer': return '/customer/tickets';
      case 'agent': return '/ops/agent/my-tickets';
      case 'foc': return '/ops/foc/tickets';
      case 'super_admin': return '/ops/admin/tickets';
      default: return '/';
    }
  };

  const handleSaveEdit = async () => {
    try {
      const changes = {};
      if (editForm.description !== ticket.description) changes.description = editForm.description;
      if (editForm.priority !== ticket.priority) changes.priority = editForm.priority;
      if (editForm.service_address !== (ticket.service_address || '')) changes.service_address = editForm.service_address;

      if (Object.keys(changes).length === 0) {
        setEditing(false);
        return;
      }

      await ticketsApi.update(id, changes);
      toast.success('Ticket updated');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update ticket');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSubmittingComment(true);
    try {
      await ticketsApi.addComment(id, comment);
      toast.success('Comment added');
      setComment('');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handlePickup = async () => {
    try {
      await ticketsApi.pickup(id);
      toast.success('Ticket picked up for review');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pick up ticket');
    }
  };

  const handleApprove = async () => {
    if (!ticket.service_address) {
      toast.error('Service address is mandatory before approval. Please edit the ticket and set it.');
      return;
    }
    try {
      await ticketsApi.approve(id);
      toast.success('Ticket approved successfully');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve ticket');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error('Please select a rejection reason');
      return;
    }
    try {
      const reason = rejectionReason === 'Other' && rejectionNote 
        ? rejectionNote 
        : rejectionReason;
      await ticketsApi.reject(id, reason);
      toast.success('Ticket rejected');
      setShowRejectDialog(false);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject ticket');
    }
  };

  const openAssignDialog = async () => {
    setAssignDialogOpen(true);
    setLoadingTechs(true);
    try {
      const { data } = await usersApi.list({ role: 'technician' });
      setTechnicians(data || []);
    } catch (e) {
      toast.error('Failed to load technicians');
    } finally {
      setLoadingTechs(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTechId) {
      toast.error('Please select a technician');
      return;
    }
    try {
      await ticketsApi.assign(id, selectedTechId);
      toast.success('Technician assigned successfully');
      setAssignDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign technician');
    }
  };

  const openReassignDialog = async (role) => {
    setReassignRole(role);
    setLoadingReassignUsers(true);
    setSelectedReassignUserId('');
    try {
      const { data } = await usersApi.list({ role });
      const activeUsers = data?.filter(u => u.is_active) || [];
      setReassignUsers(activeUsers);
    } catch (e) {
      toast.error(`Failed to load ${role}s`);
    } finally {
      setLoadingReassignUsers(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedReassignUserId) {
      toast.error(`Please select a ${reassignRole}`);
      return;
    }
    const isAssigned = 
      reassignRole === 'agent' ? !!ticket.agent_id :
      reassignRole === 'foc' ? !!ticket.foc_id :
      reassignRole === 'technician' ? !!ticket.tech_id : false;
    setSubmittingReassign(true);
    try {
      const payload = { [`${reassignRole}_id`]: selectedReassignUserId };
      await ticketsApi.reassign(id, payload);
      toast.success(`Ticket ${reassignRole} ${isAssigned ? 'reassigned' : 'assigned'} successfully`);
      setReassignRole(null);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || `Failed to assign/reassign ${reassignRole}`);
    } finally {
      setSubmittingReassign(false);
    }
  };

  const handleClose = async () => {
    try {
      await ticketsApi.close(id);
      toast.success('Ticket closed successfully');
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close ticket');
    }
  };

  const [pushingOtp, setPushingOtp] = useState(false);
  const handlePushOtp = async () => {
    if (!ticket?.job_id) {
      toast.error('No active job found for this ticket');
      return;
    }
    setPushingOtp(true);
    try {
      await jobsApi.resendOtp(ticket.job_id);
      toast.success('Close OTP sent to customer via WhatsApp');
    } catch (e) {
      toast.error('Failed to resend Close OTP');
    } finally {
      setPushingOtp(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role={currentUser?.role === 'super_admin' ? 'super_admin' : effectiveRole}>
        <div className="p-8 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-slate-200 rounded" />
            <div className="h-24 bg-slate-100 rounded-lg" />
            <div className="grid grid-cols-3 gap-4">
              <div className="h-40 bg-slate-100 rounded-lg" />
              <div className="h-40 bg-slate-100 rounded-lg" />
              <div className="h-40 bg-slate-100 rounded-lg" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout role={currentUser?.role === 'super_admin' ? 'super_admin' : effectiveRole}>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Ticket not found</p>
          <Button variant="outline" onClick={() => navigate(getBackRoute())} className="mt-4">
            Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const isAssignedAgent = ticket.agent_id === effectiveUser?.id;

  return (
    <DashboardLayout role={currentUser?.role === 'super_admin' ? 'super_admin' : effectiveRole}>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackRoute())}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-black tracking-tight">{ticket.ticket_number}</h1>
                <PriorityBadge priority={ticket.priority} />
                <StatusBadge status={ticket.status} />
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Created {new Date(ticket.created_at).toLocaleString()} by {ticket.created_by_role}
              </p>
            </div>
          </div>

          {/* Workflow Action Buttons */}
          <div className="flex gap-2 items-center flex-wrap">
            {/* Agent / Admin Pick Up / Assign Agent */}
            {isAgentOrAdmin && ticket.status === 'submitted' && (
              effectiveRole === 'super_admin' ? (
                <Button onClick={() => openReassignDialog('agent')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider">
                  <UserPlus size={15} className="mr-1.5" /> Assign Agent
                </Button>
              ) : (
                <Button onClick={handlePickup} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider">
                  <HandWaving size={15} className="mr-1.5" /> Pick Up Ticket
                </Button>
              )
            )}

            {/* Agent / Admin Approve & Reject */}
            {isAgentOrAdmin && ticket.status === 'picked_up' && (isAssignedAgent || effectiveRole === 'super_admin') && (
              <>
                <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider">
                  <CheckSquare size={15} className="mr-1.5" /> Approve
                </Button>
                <Button onClick={() => setShowRejectDialog(true)} variant="destructive" className="font-bold text-xs uppercase tracking-wider">
                  <XCircle size={15} className="mr-1.5" /> Reject
                </Button>
              </>
            )}

            {/* FOC / Admin Assign Technician */}
            {isFocOrAdmin && ticket.status === 'approved' && (
              <Button onClick={openAssignDialog} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider">
                <ShieldCheck size={15} className="mr-1.5" /> Assign Technician
              </Button>
            )}

            {/* Agent / Admin Close Ticket */}
            {isAgentOrAdmin && ticket.status !== 'closed' && ticket.status !== 'rejected' && (
              <Button onClick={handleClose} variant="destructive" className="font-bold text-xs uppercase tracking-wider" data-testid="close-ticket-btn">
                <XCircle size={15} className="mr-1.5" /> Close Ticket
              </Button>
            )}

            {/* General Edit Buttons */}
            {canEdit && (
              <>
                {!editing ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="h-9">
                    <PencilSimple size={16} className="mr-1.5" /> Edit Fields
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit} className="h-9">
                      <X size={16} className="mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveEdit} style={{ backgroundColor: brandColor }} className="text-white h-9">
                      <FloppyDisk size={16} className="mr-1" /> Save
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Flow Stepper */}
        <Card className="p-4 mb-6">
          <TicketFlowStepper ticket={ticket} />
        </Card>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="p-5">
              <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-3">Description</h3>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Select Issues
                    </Label>
                    {issueCategories && issueCategories.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        {issueCategories.map((category) => (
                          <div key={category.id} className="flex items-center space-x-2 p-2 bg-slate-50 rounded border border-slate-100">
                            <Checkbox
                              id={`edit-issue-${category.id}`}
                              checked={selectedIssueLabels.includes(category.label)}
                              onCheckedChange={() => handleIssueToggle(category.label)}
                            />
                            <label
                              htmlFor={`edit-issue-${category.id}`}
                              className="text-xs font-semibold text-slate-700 cursor-pointer select-none"
                            >
                              {category.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic mb-3">No specific issue categories found for this product.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Description details
                    </Label>
                    <Textarea
                      value={rawDescText}
                      onChange={(e) => handleRawDescChange(e.target.value)}
                      rows={4}
                      placeholder="Enter description..."
                    />
                  </div>
                  <div className="pt-2 border-t">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Compiled Preview
                    </Label>
                    <pre className="text-xs bg-slate-50 p-2.5 rounded font-mono text-slate-600 max-h-32 overflow-y-auto border whitespace-pre-wrap">
                      {editForm.description}
                    </pre>
                  </div>
                </div>
              ) : (
                <IssueDescription description={ticket.description} />
              )}
            </Card>

            {/* Service Location */}
            <Card className="p-5">
              <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-3">
                <MapPin size={14} className="inline mr-1" /> Service Location
              </h3>
              {editing ? (
                <Input
                  value={editForm.service_address}
                  onChange={(e) => setEditForm({ ...editForm, service_address: e.target.value })}
                  placeholder="Enter service address (mandatory before approval)"
                />
              ) : (
                <p className="text-sm font-semibold">
                  {ticket.service_address || (
                    <span className="text-amber-600 font-medium">⚠ Not set — required before approval</span>
                  )}
                </p>
              )}
              {ticket.customer_lat && ticket.customer_lng && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs font-mono font-medium text-slate-400">
                    GPS: {ticket.customer_lat}, {ticket.customer_lng}
                  </span>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${ticket.customer_lat},${ticket.customer_lng}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                    data-testid="ticket-gmap-link"
                  >
                    <MapPin size={14} weight="bold" /> Navigate on Google Maps
                  </a>
                </div>
              )}
            </Card>


            {/* Priority (editable) */}
            {editing && (
              <Card className="p-5">
                <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-3">Priority</h3>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </Card>
            )}

            {/* Service Checklist (after technician completes job) */}
            {ticket.job_checklist && ticket.job_checklist.length > 0 && (
              <Card className="p-5">
                <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                  <ClipboardText size={14} className="inline" /> Service Checklist
                </h3>
                <div className="space-y-2">
                  {ticket.job_checklist.map((item, i) => (
                    <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded-lg ${
                      item.is_checked ? 'bg-emerald-50 border border-emerald-100' : 'bg-slate-50 border border-slate-100'
                    }`}>
                      <div className={`w-4 h-4 rounded mt-0.5 shrink-0 flex items-center justify-center ${
                        item.is_checked ? 'bg-emerald-500' : 'bg-slate-200'
                      }`}>
                        {item.is_checked && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 12 12">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold ${
                          item.is_checked ? 'text-emerald-800' : 'text-slate-500 line-through'
                        }`}>
                          {item.label}
                          {item.is_mandatory && <span className="ml-1 text-red-500 text-[9px] uppercase font-bold">*req</span>}
                        </p>
                        {item.note && (
                          <p className="text-[11px] text-slate-500 italic mt-0.5">{item.note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Technician Job Report (Fault Notes, Photos, Payment, GPS) */}
            {ticket.job_id && (ticket.job_fault_notes || (ticket.job_photos && ticket.job_photos.length > 0) || ticket.job_payment || ticket.job_start_time) && (
              <Card className="p-5 border border-slate-200 bg-white shadow-sm space-y-6">
                <div className="border-b pb-3 flex items-center justify-between">
                  <h3 className="font-bold text-sm uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <ClipboardText size={18} className="text-primary" weight="fill" />
                    Technician Service Report
                  </h3>
                  <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold font-mono">
                    Job ID: {ticket.job_id.slice(0, 8)}
                  </span>
                </div>

                {/* Fault Notes */}
                {ticket.job_fault_notes && (
                  <div className="space-y-1.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Diagnosis & Fault Notes</h4>
                    <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-100 text-sm text-slate-750 font-medium whitespace-pre-wrap leading-relaxed">
                      {ticket.job_fault_notes}
                    </div>
                  </div>
                )}

                {/* Completion Photos */}
                {ticket.job_photos && ticket.job_photos.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Service Completion Photos</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {ticket.job_photos.map((photo, i) => (
                        <div key={i} className="group relative aspect-video rounded-lg overflow-hidden border bg-slate-50 hover:shadow-md transition-all">
                          <img 
                            src={photo} 
                            alt={`Job work verification ${i + 1}`} 
                            className="object-cover w-full h-full cursor-zoom-in group-hover:scale-105 transition-transform duration-200"
                            onClick={() => window.open(photo, '_blank')}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                            <span className="text-white text-[10px] font-bold uppercase tracking-wider bg-black/60 px-2 py-1 rounded">
                              View Full Size
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Information */}
                {ticket.job_payment ? (() => {
                  const pay = ticket.job_payment;
                  let refText = '';
                  let photoUrl = '';
                  try {
                    const parsed = JSON.parse(pay.reference);
                    refText = parsed.ref || '';
                    photoUrl = parsed.photo || '';
                  } catch (e) {
                    if (pay.reference?.startsWith('http')) {
                      photoUrl = pay.reference;
                      refText = 'Screenshot Proof';
                    } else {
                      refText = pay.reference || '';
                    }
                  }

                  return (
                    <div className="space-y-2.5 pt-4 border-t border-slate-100">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Payment Collection</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500 font-medium">Collected Amount:</span>
                            <span className="font-bold text-slate-800 text-sm">₹{pay.amount}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500 font-medium">Payment Method:</span>
                            <span className="font-bold text-slate-800 uppercase bg-slate-200/60 px-2 py-0.5 rounded text-[10px]">
                              {pay.method}
                            </span>
                          </div>
                          {refText && (
                            <div className="flex justify-between items-start text-xs pt-1 border-t border-slate-100/60">
                              <span className="text-slate-500 font-medium">Reference:</span>
                              <span className="font-mono font-bold text-slate-700 max-w-[150px] truncate" title={refText}>
                                {refText}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center text-xs text-slate-400">
                            <span>Collected At:</span>
                            <span>{new Date(pay.collected_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {photoUrl && (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between max-w-[200px] mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">UPI Transaction Proof</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-500 hover:text-slate-900 rounded-full"
                                onClick={() => setPaymentRotation(r => (r + 90) % 360)}
                                title="Rotate screenshot"
                              >
                                <ArrowClockwise size={12} weight="bold" />
                              </Button>
                            </div>
                            <div 
                              className="relative aspect-video rounded-lg overflow-hidden border bg-slate-50 max-w-[200px] hover:shadow-md group cursor-pointer"
                              onClick={() => setLightboxPhotoUrl(photoUrl)}
                            >
                              <img 
                                src={photoUrl} 
                                alt="UPI Payment Screenshot" 
                                className="object-cover w-full h-full transition-all duration-200"
                                style={{ transform: `rotate(${paymentRotation}deg)` }}
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                                <span className="text-white text-[9px] font-bold uppercase tracking-wider bg-black/60 px-1.5 py-0.5 rounded">
                                  View proof
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })() : (
                  <div className="space-y-1.5 pt-4 border-t border-slate-100 text-xs flex justify-between items-center">
                    <span className="font-bold uppercase tracking-wider text-slate-400">Payment Collection</span>
                    <span className="text-slate-500 font-semibold italic">No payment details recorded</span>
                  </div>
                )}

                {/* GPS and Timestamps */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100 text-xs">
                  <div className="space-y-2">
                    <h5 className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Service Timing</h5>
                    <div className="space-y-1">
                      {ticket.job_start_time && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Started:</span>
                          <span className="font-bold text-slate-700">
                            {new Date(ticket.job_start_time).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {ticket.job_end_time && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Finished:</span>
                          <span className="font-bold text-slate-700">
                            {new Date(ticket.job_end_time).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h5 className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Technician GPS Tracking</h5>
                    <div className="space-y-1">
                      {ticket.job_start_gps_lat && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400">Arrival GPS:</span>
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${ticket.job_start_gps_lat},${ticket.job_start_gps_lng}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-bold text-primary hover:underline flex items-center gap-1 font-mono text-[11px]"
                          >
                            <MapPin size={12} weight="bold" /> {ticket.job_start_gps_lat.toFixed(5)}, {ticket.job_start_gps_lng.toFixed(5)}
                          </a>
                        </div>
                      )}
                      {ticket.job_end_gps_lat && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400">Completion GPS:</span>
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${ticket.job_end_gps_lat},${ticket.job_end_gps_lng}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-bold text-primary hover:underline flex items-center gap-1 font-mono text-[11px]"
                          >
                            <MapPin size={12} weight="bold" /> {ticket.job_end_gps_lat.toFixed(5)}, {ticket.job_end_gps_lng.toFixed(5)}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Timeline & Comments */}
            <Card className="p-5">

              <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-4">
                <Clock size={14} className="inline mr-1" /> Activity Timeline
              </h3>
              
              {/* Comment Input */}
              <div className="flex gap-2 mb-6 p-3 bg-slate-50 rounded-lg">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={2}
                  className="flex-1 bg-white"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!comment.trim() || submittingComment}
                  size="sm"
                  className="self-end"
                  style={{ backgroundColor: brandColor }}
                >
                  <ChatCircle size={16} weight="fill" />
                </Button>
              </div>

              {/* Events */}
              <div className="space-y-4">
                {[...(ticket.events || [])].reverse().map((event) => (
                  <div key={event.id} className="flex gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                      event.event_type === 'edited' ? 'bg-amber-400' :
                      event.event_type === 'note_added' ? 'bg-blue-400' :
                      event.event_type === 'rejected' ? 'bg-rose-400' :
                      'bg-emerald-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                          {event.event_type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          by {event.actor_role}
                        </span>
                        <span className="text-[10px] text-slate-300 ml-auto">
                          {new Date(event.created_at).toLocaleString()}
                        </span>
                      </div>
                      {event.note && (
                        <p className="text-sm text-slate-600 mt-0.5">{event.note}</p>
                      )}
                      {/* Show edit diff */}
                      {event.event_type === 'edited' && event.metadata?.changes && (
                        <div className="mt-1 text-xs bg-amber-50 rounded p-2 border border-amber-100">
                          {Object.entries(event.metadata.changes).map(([field, diff]) => (
                            <div key={field} className="flex gap-2">
                              <span className="font-bold text-slate-500">{field}:</span>
                              <span className="text-rose-500 line-through">{diff.before || '(empty)'}</span>
                              <span className="text-slate-400">→</span>
                              <span className="text-emerald-600">{diff.after}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Start OTP (when tech has arrived) */}
            {(effectiveRole === 'super_admin' || effectiveRole === 'customer') && ticket.start_otp && (
              <Card className="p-4 border-emerald-200 bg-emerald-50 shadow-sm">
                <h3 className="font-bold text-xs uppercase tracking-wider text-emerald-800 mb-2 flex items-center gap-1.5">
                  <ShieldCheck size={14} weight="bold" /> Start OTP (When tech arrives)
                </h3>
                <p className="text-3xl font-black tracking-wider text-emerald-950 font-mono mt-1 select-all">
                  {ticket.start_otp}
                </p>
                <p className="text-[10px] text-emerald-700 mt-1.5 leading-normal">
                  The customer shares this OTP with the technician to begin the service job.
                </p>
              </Card>
            )}

            {/* Verification OTP (Admins & Customers only) */}
            {(effectiveRole === 'super_admin' || effectiveRole === 'customer') && ticket.close_otp && (
              <Card className="p-4 border-amber-200 bg-amber-50 shadow-sm">
                <h3 className="font-bold text-xs uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1.5">
                  <ShieldCheck size={14} weight="bold" /> Close OTP (When job is done)
                </h3>
                <p className="text-3xl font-black tracking-wider text-amber-950 font-mono mt-1 select-all">
                  {ticket.close_otp}
                </p>
                {effectiveRole !== 'customer' ? (
                  <>
                    <p className="text-[10px] text-amber-700 mt-1.5 leading-normal">
                      Ask the customer to share this OTP only when they are satisfied and the job is finished.
                    </p>
                    <Button 
                      onClick={handlePushOtp}
                      disabled={pushingOtp}
                      size="sm"
                      variant="outline"
                      className="w-full mt-3 bg-white border-amber-300 hover:bg-amber-100 text-amber-900 font-bold text-[10px] uppercase tracking-wider h-8 flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      <HandWaving size={14} /> {pushingOtp ? 'Sending...' : 'Push OTP via WhatsApp'}
                    </Button>
                  </>
                ) : (
                  <p className="text-[10px] text-amber-700 mt-1.5 leading-normal">
                    Share this OTP with the technician when the job is complete and you are satisfied.
                  </p>
                )}
              </Card>
            )}

            {/* Customer Info */}
            <Card className="p-4">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3">
                <User size={12} className="inline mr-1" /> Customer
              </h3>
              <p className="font-semibold text-sm">{ticket.customer_name}</p>
              {ticket.customer_phone && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Phone size={12} /> {ticket.customer_phone}
                </p>
              )}
            </Card>

            {/* Product Info */}
            <Card className="p-4">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3">
                <Package size={12} className="inline mr-1" /> Product
              </h3>
              <p className="font-semibold text-sm">{ticket.product_name}</p>
              <p className="text-xs text-muted-foreground mt-1">{ticket.issue_category}</p>
            </Card>

            {/* SLA */}
            <Card className="p-4">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3">
                <Clock size={12} className="inline mr-1" /> SLA
              </h3>
              <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
            </Card>

            {/* Assigned Staff */}
            <Card className="p-4">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3">
                <ShieldCheck size={12} className="inline mr-1" /> Assigned Staff
              </h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Agent</span>
                  <div className="flex items-center gap-1.5 font-medium">
                    <span>{ticket.agent_name || '—'}</span>
                    {effectiveRole === 'super_admin' && (
                      <button 
                        onClick={() => openReassignDialog('agent')} 
                        className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold uppercase transition-colors"
                        data-testid="reassign-agent-btn"
                      >
                        (Change)
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">FOC</span>
                  <div className="flex items-center gap-1.5 font-medium">
                    <span>{ticket.foc_name || '—'}</span>
                    {(effectiveRole === 'super_admin' || effectiveRole === 'agent') && (
                      <button 
                        onClick={() => openReassignDialog('foc')} 
                        className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold uppercase transition-colors"
                        data-testid="reassign-foc-btn"
                      >
                        (Change)
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Technician</span>
                  <div className="flex items-center gap-1.5 font-medium">
                    <span>{ticket.tech_name || '—'}</span>
                    {(effectiveRole === 'super_admin' || effectiveRole === 'agent' || effectiveRole === 'foc') && (
                      <button 
                        onClick={() => openReassignDialog('technician')} 
                        className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold uppercase transition-colors"
                        data-testid="reassign-tech-btn"
                      >
                        (Change)
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Customer Reviews */}
            {ticket.is_rated && (
              <Card className="p-4">
                <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                  <Star size={12} className="text-amber-500" weight="fill" /> Customer Reviews
                </h3>
                <div className="space-y-3">
                  {ticket.agent_name && (
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Agent</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={12} className={s <= (ticket.agent_stars || 0) ? 'text-amber-400' : 'text-slate-200'} weight="fill" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-slate-700">{ticket.agent_name}</p>
                      {ticket.agent_comment && ticket.agent_comment !== 'None' && (
                        <p className="text-xs text-slate-500 italic">"{ticket.agent_comment}"</p>
                      )}
                    </div>
                  )}
                  {ticket.tech_name && (
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Technician</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={12} className={s <= (ticket.tech_stars || 0) ? 'text-amber-400' : 'text-slate-200'} weight="fill" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-slate-700">{ticket.tech_name}</p>
                      {ticket.tech_comment && ticket.tech_comment !== 'None' && (
                        <p className="text-xs text-slate-500 italic">"{ticket.tech_comment}"</p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Dates */}
            <Card className="p-4">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-3">
                <CalendarBlank size={12} className="inline mr-1" /> Timeline
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
                {ticket.resolved_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Resolved</span>
                    <span>{new Date(ticket.resolved_at).toLocaleDateString()}</span>
                  </div>
                )}
                {ticket.closed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Closed</span>
                    <span>{new Date(ticket.closed_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Reject Ticket Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-slate-600">Select Rejection Reason</Label>
              <Select value={rejectionReason} onValueChange={setRejectionReason}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REJECTION_REASONS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rejectionReason === 'Other' && (
              <div className="space-y-1">
                <Label htmlFor="reject-note" className="text-xs font-bold text-slate-600">Explain Reason</Label>
                <Textarea
                  id="reject-note"
                  placeholder="Enter rejection explanation..."
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleReject} disabled={!rejectionReason}>
              Reject Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Technician Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Field Technician</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-slate-600">Select Technician</Label>
              {loadingTechs ? (
                <div className="h-10 bg-slate-100 rounded animate-pulse" />
              ) : technicians.length > 0 ? (
                <Select value={selectedTechId} onValueChange={setSelectedTechId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose active technician" />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.full_name} ({t.phone})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-red-500">No active technicians available</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAssign} disabled={loadingTechs || !selectedTechId} style={{ backgroundColor: brandColor }} className="text-white">
              Confirm Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignRole} onOpenChange={(open) => !open && setReassignRole(null)}>
        <DialogContent className="max-w-md">
          {(() => {
            const isAssigned = 
              reassignRole === 'agent' ? !!ticket?.agent_id :
              reassignRole === 'foc' ? !!ticket?.foc_id :
              reassignRole === 'technician' ? !!ticket?.tech_id : false;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="capitalize">{isAssigned ? 'Reassign' : 'Assign'} {reassignRole}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-slate-600 capitalize">Select {reassignRole}</Label>
                    {loadingReassignUsers ? (
                      <div className="h-10 bg-slate-100 rounded animate-pulse" />
                    ) : reassignUsers.length > 0 ? (
                      <Select value={selectedReassignUserId} onValueChange={setSelectedReassignUserId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={`Choose active ${reassignRole}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {reassignUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.full_name} ({u.phone})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-red-500">No active {reassignRole}s available</p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setReassignRole(null)}>
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleReassign} 
                    disabled={loadingReassignUsers || !selectedReassignUserId || submittingReassign} 
                    style={{ backgroundColor: brandColor }} 
                    className="text-white font-bold"
                  >
                    {submittingReassign 
                      ? (isAssigned ? 'Reassigning...' : 'Assigning...') 
                      : (isAssigned ? 'Confirm Reassignment' : 'Confirm Assignment')}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* Fullscreen Image Lightbox Overlay */}
      {lightboxPhotoUrl && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-sm animate-fade-in">
          {/* Header Controls */}
          <div className="absolute top-4 right-4 flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              className="h-9 px-3 text-white bg-white/10 hover:bg-white/20 hover:text-white rounded-full flex items-center gap-1.5 text-xs font-semibold"
              onClick={() => setPaymentRotation(r => (r + 90) % 360)}
            >
              <ArrowClockwise size={16} weight="bold" />
              Rotate
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-9 w-9 text-white bg-white/10 hover:bg-white/20 hover:text-white rounded-full flex items-center justify-center"
              onClick={() => setLightboxPhotoUrl(null)}
            >
              <X size={20} weight="bold" />
            </Button>
          </div>
          {/* Image Container */}
          <div className="max-w-full max-h-[85vh] flex items-center justify-center overflow-hidden transition-all duration-300">
            <img
              src={lightboxPhotoUrl}
              alt="Payment proof fullscreen"
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg transition-transform duration-200 ease-out"
              style={{ transform: `rotate(${paymentRotation}deg)` }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};
